import React, {useState, useEffect} from 'react'
import { Card, Button, Form, message, Spin, Result } from 'antd';
import Page1 from "./Page1";
import Page2 from "./Page2";
import Page3 from "./Page3";

import {userService} from "../../appRedux/services";
import { CONSULTANT_ONBOARDING } from "../../constants/ApiEndpoints";
import {fetchStart, fetchSuccess} from "../../appRedux/actions";
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { compressImage } from '../../util/CommonUtils/CompressImage';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';
import { fetchOnboardingFields, fetchOnboardingFieldsSuccess } from '../../appRedux/actions/Essentials';

function Onboarding() {
    const [currentPage, setCurrentPage] = useState(1);
    const [formData, setFormData] = useState({});
    const [form] = Form.useForm();
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState({status: true, errorMessage: null});
    const [allowedPages, setAllowedPages] = useState([]);
    
    const onboardingFields = useSelector(state => state.essentials.onboardingFields);
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const onboarding_bgc_tokenFromUrl = searchParams.get('onboarding_bgc_token');
    const key = searchParams.get('key');
    const tokenFromUrl = searchParams.get('token');

    const pageFieldMap = {
        1:['basic_details', 'emergency_details'],
        2:['education_details'],
        3:['legalities']
    };
    
    const validateFormPage = (pageNumber, onboardingFields) => {
        const fields = pageFieldMap[pageNumber] || [];
        return fields.some(field => onboardingFields.includes(field));
    };

    const getValidPages = (onboardingFields) => {
        return Object.keys(pageFieldMap)
            .map(Number)
            .filter(pageNumber => validateFormPage(pageNumber, onboardingFields));
    };
    
    useEffect(() => {
        if (onboardingFields && onboardingFields.length > 0) {
            const validPages = getValidPages(onboardingFields);
            if (validPages.length > 0) {
                setCurrentPage(validPages[0]);
                setAllowedPages(validPages);
            } else {
                setPageLoading({ status: false, errorMessage: { error: 'Oops! Something went wrong' } });
            }
        }
    }, [onboardingFields]);

    const nextPage = () => {
        form.validateFields()
        .then( async values => {
            if(values && values.attachment_type && values.attachment_type === 'state_id' && values.state_id && values.state_id.fileList && values.state_id.fileList.length === 0 ){
                message.error("Add Attach State ID")
                return
            }
            if(values && values.attachment_type && values.attachment_type === 'driving_license' && values.driving_license_front && values.driving_license_front.fileList && values.driving_license_front.fileList.length === 0 ){
                message.error("Driving License (Front)")
                return
            }
            if(values && values.attachment_type && values.attachment_type === 'driving_license' && values.driving_license_back && values.driving_license_back.fileList && values.driving_license_back.fileList.length === 0 ){
                message.error("Driving License (Back)")
                return
            }
        /////
            const compressFiles = async (field, fileList) => {
              return Promise.all(fileList.map(async file => {
                if (file.originFileObj.size <= 1048576) {  // this is the size of 1MB in bytes
                    return file;
                }
                const compressedFile = await compressImage(file.originFileObj);

                return { ...file, originFileObj: compressedFile };
              }));
            };
            const fileFields = ['driving_license_front','state_id','driving_license_back'];
            for (const field of fileFields) {
              if (values[field] && values[field].fileList) {
                values[field].fileList = await compressFiles(field, values[field].fileList);
              }
            }
        /////
            localStorage.setItem('formData', JSON.stringify({ ...formData, ...values }));

            if(values && values.visa){
                setFormData({ ...formData, visa: values.visa })
            }

            if (allowedPages && currentPage !== undefined) {
              const sortedPages = [...allowedPages].sort((a, b) => a - b);
              const nextPageToShow = sortedPages.find(page => page > currentPage);

              if (nextPageToShow !== undefined) {
                setCurrentPage(nextPageToShow);
              } else {
                console.log('No next page found');
              }
            }
        })
        .catch(errorInfo => {
            console.error("Validation failed:", errorInfo);
        });
    };
  
    const prevPage = () => {
        localStorage.setItem('formData', JSON.stringify({ ...formData}));
        if (allowedPages && currentPage !== undefined) {
            const sortedPages = [...allowedPages].sort((a, b) => a - b);
            const nextPageToShow = sortedPages.find(page => page < currentPage);
            if (nextPageToShow !== undefined) {
                setCurrentPage(nextPageToShow);
            } else {
                console.log('No next page found');
            }
        }
    };

    useEffect(() => {
        const storedData = localStorage.getItem('formData');
        if (storedData) {
            const formData = JSON.parse(storedData);
            delete formData.state_id;
            delete formData.driving_license_front;
            delete formData.driving_license_back;
            delete formData.gc_copy; 
            delete formData.gc_ead_copy;
            delete formData.us_passport_copy;
            delete formData.w2_contract;
            delete formData.h4_ead_copy;
            delete formData.opt_copy;
            delete formData.cpt_copy;
            delete formData.other_copy;
            delete formData.naturalization_certificate;
            delete formData.us_passport_card;
            delete formData.work_permit;
            localStorage.setItem('formData', JSON.stringify({ ...formData}));
        }
    }, []);

    const getApiEndpoint = () => {
        const currentDomain = window.location.hostname;
        if (currentDomain === 'dev.stell.one') {
            return "https://dev-backend.stell.one/api/consultants/on-boarding/";
        } 
        if (currentDomain === 'stell.one') {
            return "https://prod-backend.stell.one/api/consultants/on-boarding/";
        }
        if (currentDomain === 'staging.stell.one') {
            return "https://backend.stell.one/api/consultants/on-boarding/";
        }
        if (currentDomain === 'localhost' || currentDomain === '127.0.0.1' || currentDomain === '0.0.0.0') {
            return "http://localhost:8000/api/consultants/on-boarding/";
        }
        return "https://backend.stell.one/api/consultants/on-boarding/";
    };

    // Helper function to make API calls
    const makeApiCall = async (method, data = null) => {
        const endpoint = getApiEndpoint();
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };

        if (method === 'GET') {
            return axios.get(`${endpoint}?key=${key}&onboarding_bgc_token=${onboarding_bgc_tokenFromUrl}`);
        } else if (method === 'PATCH') {
            return axios.patch(endpoint, data, config);
        }
    };

    useEffect(() => {
        const fetchFields = async () => {
            if (onboarding_bgc_tokenFromUrl && key) {
                try {
                    setPageLoading({status: true, errorMessage: null})
                    const response = await makeApiCall('GET');
                    if (response && response.data) {
                        if(response?.data?.fields){
                            dispatch(fetchOnboardingFieldsSuccess(response.data));
                            setPageLoading({status: false, errorMessage: null})
                        }else{
                            setPageLoading({status: false, errorMessage: {error:'Oops! Something went wrong'}})
                        }
                    }
                } catch (error) {
                    console.error('Error fetching onboarding fields:', error);
                    setPageLoading({status:false, errorMessage: error?.response?.data})
                }
            }
        };
        fetchFields();
    }, [onboarding_bgc_tokenFromUrl, key, dispatch]);
    
    useEffect(()=>{
        const storedData = localStorage.getItem('formData');
        if(storedData === null ){
            setFormData({})
            form.resetFields();
        }
      },[currentPage])

    let timeoutId = null;

    const throttledHandleSubmit = () => {
        if (timeoutId) clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                handleSubmit();
                timeoutId = null;
            }, 500);
    };

      const handleSubmit = ()=>{
        form.validateFields()
        .then( async values => {
            if(values && values.gc_ead_copy && values.gc_ead_copy.fileList && values.gc_ead_copy.fileList.length === 0 ){
                message.error("Add Attach GC EAD Copy")
                return
            }
            // if(values && values.w2_contract && values.w2_contract.fileList && values.w2_contract.fileList.length === 0 ){
            //     message.error("Add Attach W-2 Contract With American Chase")
            //     return
            // }
            if(values && values.h4_ead_copy && values.h4_ead_copy.fileList && values.h4_ead_copy.fileList.length === 0 ){
                message.error("Add Attach H4 EAD Copy")
                return
            }
            if(values && values.opt_copy && values.opt_copy.fileList && values.opt_copy.fileList.length === 0 ){
                message.error("Add Attach OPT Copy")
                return
            }
            if(values && values.cpt_copy && values.cpt_copy.fileList && values.cpt_copy.fileList.length === 0 ){
                message.error("Add Attach CPT Copy")
                return
            }
            if(values && values.other_copy && values.other_copy.fileList && values.other_copy.fileList.length === 0 ){
                message.error("Add Attach Other Copy")
                return
            }
            ////
            const compressFiles = async (field, fileList) => {
              return Promise.all(fileList.map(async file => {
                if (file.originFileObj.size <= 1048576) {  // this is the size of 1MB in bytes
                    return file;
                }
                const compressedFile = await compressImage(file.originFileObj);
                return { ...file, originFileObj: compressedFile };
              }));
            };

            const fileFields = ['gc_ead_copy', 'w2_contract', 'h4_ead_copy', 'opt_copy', 'cpt_copy', 'other_copy','us_passport_copy','gc_copy', 'naturalization_certificate', 'us_passport_card', 'work_permit', 'state_id'];
            for (const field of fileFields) {
              if (values[field] && values[field].fileList) {
                 values[field].fileList = await compressFiles(field, values[field].fileList);
              }
            }
            ////
        const transformDataForPost = (formData) => {
            // Create a new object to store only non-empty values
            const filteredData = {};

            // Handle phone number formatting
            if((formData && !formData.ext_Code || formData.ext_Code === "") && formData.countryCode && formData.phone){
                const formattedPhone = `${formData.countryCode} ${formData.phone}`;
                filteredData.phone_number = formattedPhone;
            }
            if(formData && formData.ext_Code && formData.countryCode && formData.phone){
                const formattedPhone = `${formData.countryCode} ${formData.phone}, ${formData.ext_Code}`;
                filteredData.phone_number = formattedPhone;
            }
            // Helper function to check if a value is empty
            const isEmpty = (value) => {
                if (value === undefined || value === null || value === "") return true;
                if (typeof value === "object") {
                    if (Array.isArray(value)) return value.length === 0;
                    return Object.keys(value).length === 0;
                }
                return false;
            };

            // Process each field and only add non-empty values
            const processField = (key, value) => {
                if (!isEmpty(value)) {
                    if (key === 'dob' && dayjs(value).isValid()) {
                        filteredData[key] = dayjs(value).format('YYYY-MM-DD');
                    } else if (key === 'visa') {
                        filteredData[key] = value === 'Other' ? formData.othervisa : value;
                    } else if (key === 'alternate_emails' && formData.alternate_emails && formData.alternate_emails.email1) {
                        const filteredEmails = [formData.alternate_emails.email1].filter(email => email && email.trim() !== "");
                        if (filteredEmails.length > 0) {
                            filteredData[key] = filteredEmails;
                            filteredData.alternate_email = filteredEmails[0];
                        }
                    } else {
                        filteredData[key] = value;
                    }
                }
            };

            Object.entries(formData).forEach(([key, value]) => {
                if (!['ext_Code', 'phone', 'countryCode', 'degree_1', 'institute_1', 'university_1', 'area_of_study_1', 
                     'start_year_1', 'end_year_1', 'degree_2', 'institute_2', 'university_2', 'area_of_study_2', 
                     'start_year_2', 'end_year_2', 'emergency_name', 'emergency_email', 'emergency_phone', 
                     'emergency_relation'].includes(key)) {
                    processField(key, value);
                }
            });

            const bachelorsData = {
                degree: formData.degree_1,
                institute: formData.institute_1,
                university: formData.university_1,
                area_of_study: formData.area_of_study_1,
                ...(formData.start_year_1 && { start_year: dayjs(formData.start_year_1).format('YYYY-MM-DD') }),
                ...(formData.end_year_1 && { end_year: dayjs(formData.end_year_1).format('YYYY-MM-DD') })
            };

            const mastersData = {
                degree: formData.degree_2,
                institute: formData.institute_2,
                university: formData.university_2,
                area_of_study: formData.area_of_study_2,
                ...(formData.start_year_2 && { start_year: dayjs(formData.start_year_2).format('YYYY-MM-DD') }),
                ...(formData.end_year_2 && { end_year: dayjs(formData.end_year_2).format('YYYY-MM-DD') })
            };

            if (Object.values(bachelorsData).some(value => !isEmpty(value))) {
                filteredData.bachelors = JSON.stringify(bachelorsData);
            }
            if (Object.values(mastersData).some(value => !isEmpty(value))) {
                filteredData.masters = JSON.stringify(mastersData);
            }

            if (formData.emergency_name || formData.emergency_email || formData.emergency_phone || formData.emergency_relation) {
                const emergencyContactsData = {
                    name: formData.emergency_name || '',
                    email: formData.emergency_email || '',
                    contact: formData.emergency_phone || '',
                    relation: formData.emergency_relation || ''
                };
                filteredData.emergency_contacts = JSON.stringify(emergencyContactsData);
            }

            return filteredData;
        };

        
        const postData = transformDataForPost(formData);
        function convertToDataToSend(postData) {
            const dataToSend = new FormData();
        
            Object.entries(postData).forEach(([key, value]) => {
                if(key === 'visa_at_us_entry' && value === ""){
                    dataToSend.append(key, "");
                    return
                }
                if(key === 'current_visa_source' && value === ""){
                    dataToSend.append(key, "");
                    return
                }
                if (value == undefined || value == "" || typeof value == undefined || value == "undefined" || value === null) {
                    return;
                } else if (value && value.fileList) {
                    // Iterate through file list and append each file to the FormData
                    value.fileList.forEach(file => {
                        if (file.originFileObj) {
                            dataToSend.append(key, file.originFileObj);
                        }
                    });
                } else if (typeof value === 'object' && value !== null) {

                    if (key === 'education_details' || key === 'emergency_contacts') {
                        dataToSend.append(key, value);
                    } else {
                        if (value.fileList && value.fileList.length > 0) {
                            value.fileList.forEach(file => {
                                if (file.originFileObj) {
                                    dataToSend.append(key, file.originFileObj);
                                }
                            });
                        }
                    }
                } else {
                    dataToSend.append(key, value);
                }
            });
            dataToSend.append('email', key ? atob(key) : "");
            dataToSend.append('token', tokenFromUrl ? atob(tokenFromUrl) : "");
            dataToSend.append('onboarding_bgc_token', onboarding_bgc_tokenFromUrl);
            return dataToSend;
        }
        
        const uploadData = convertToDataToSend(postData);

            dispatch(fetchStart());
            setLoading(true);

        try {
            const response = await makeApiCall('PATCH', uploadData);
            setLoading(false);
                dispatch(fetchSuccess());
                message.success('Form Submitted Successfully!');
                localStorage.removeItem('formData');
                // setCurrentPage(1)
                form.resetFields();
            
                // setTimeout(()=>{
                //     window.location.reload()
                // },200)
                setPageLoading({ status: false, errorMessage: null, successMessage: { message: 'Form submitted successfully!' } })
            }catch(error) {
                setLoading(false)
                let error_msg = 'Error Submitting the form!'
                if (error && error.response && error.response.data) {
                    const errorData = error.response.data
                    if(errorData.errors && typeof errorData.errors === 'object'){
                        const errorkeys = Object.keys(errorData.errors);
                        errorkeys.forEach((key) => {
                          error_msg = errorData.errors[key];
                        });
                    }else if(errorData.error && typeof errorData.error === 'string'){
                        error_msg = errorData.error;
                    }
                }
                message.error(error_msg);
            }
        })
        .catch(errorInfo => {
            setLoading(false)
            console.error("Validation failed:", errorInfo);
        });
      }

    if (!pageLoading.status && !pageLoading.errorMessage && pageLoading.successMessage) {
        return (
            <div style={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <Result
                    status="success"
                    // title="Welcome to American Chase"
                    subTitle={pageLoading?.successMessage?.message || 'Form submitted successfully!'}
                />
            </div>
        );
    }

    if (pageLoading.status && !pageLoading.errorMessage) {
        return (
            <div style={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
            <Result
                icon={<Spin size="large" />}
                title="Welcome to American Chase"
                subTitle="Loading... please wait"
            />
            </div>
        );
    }

    if (!pageLoading.status && pageLoading.errorMessage) {
        return (
            <div style={{ display: 'flex', height: '80vh', alignItems: 'center', justifyContent: 'center' }}>
                <Result
                    status="error"
                    title="Oops! Something went wrong"
                    subTitle={
                    typeof pageLoading.errorMessage?.error === 'string'
                        ? <>
                        <div>
                            Invalid URL - The URL you requested is not valid or has expired.
                        </div>
                        <div>
                            If you continue to experience issues, contact our support team or your hiring coordinator for assistance.
                        </div>
                        </>
                        : 'An unexpected error occurred. Please try again later.'
                    }
                />
            </div>
        );
    }

  return (<>
    {pageLoading && pageLoading.status === true && !pageLoading.errorMessage ? 
    'Wellcome to American Chase. Loading.. please wait' 
    :
    pageLoading.status === false && pageLoading.errorMessage ? 
      pageLoading.errorMessage.error && typeof pageLoading.errorMessage.error === 'string' ?  
        pageLoading.errorMessage.error 
        :
        "Something went wrong"
    :
    <div style={styles.div}>
            <Card style={styles.card} bodyStyle={styles.cardBody}>
                <div style={{width:"100%",textAlign:"center",fontWeight:"bold",fontSize:"20px"}}>Onboarding Information With American Chase</div>
                <div style={{width:"100%",textAlign:"center", fontSize:"15px"}}>Congratulations on your selection with American Chase! To proceed, kindly complete the form as soon as possible.</div>
                {currentPage === 1 && (
                    <Page1 formData={formData} setFormData={setFormData} form={form} onboardingFields={onboardingFields} />
                )}
                {currentPage === 2 && (
                    <Page2 formData={formData} setFormData={setFormData} form={form} onboardingFields={onboardingFields} />
                )}
                {currentPage === 3 && (
                    <Page3 formData={formData} setFormData={setFormData} form={form} onboardingFields={onboardingFields} />
                )}
                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between' }}>
                    {currentPage > Math.min(...allowedPages) && (
                        <Button onClick={prevPage}>Back</Button>
                    )}
                    {currentPage < Math.max(...allowedPages) && (
                        <Button onClick={nextPage}>Next</Button>
                    )}
                    {currentPage === Math.max(...allowedPages) && (
                        <Button disabled={loading} type="primary" onClick={throttledHandleSubmit}>
                        {loading ? 'Submitting...' : 'Submit'}
                        </Button>
                    )}
                </div>
            </Card>
        </div>
        }
  </>)
}

export default Onboarding
const styles = {
    card: {
    //   margin:"2% 10% 0% 10%"
      margin:"2% 2% 0% 2%"
    },
    div : {
        overflow:'auto',
        height:'100vh' 
    }
  };